import React, { useEffect, useRef } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { useUserStore } from "@/stores/userStore";
import { Loader2 } from "lucide-react";

interface RequireAuthProps {
  children?: React.ReactNode;
}

export default function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const isLoading = useUserStore((state) => state.isLoading);
  const location = useLocation();

  // 使用 ref 防止重复调用 storeLocation
  const hasStoredLocationRef = useRef(false);

  // 只在未认证且确实需要重定向时存储位置（仅调用一次）
  useEffect(() => {
    if (!isAuthenticated && !isLoading && !hasStoredLocationRef.current) {
      hasStoredLocationRef.current = true;

      const storeLocation = async () => {
        try {
          await fetch("/users/store_location", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token":
                document.querySelector<HTMLMetaElement>(
                  'meta[name="csrf-token"]'
                )?.content || "",
            },
            credentials: "same-origin",
            body: JSON.stringify({
              return_to: location.pathname + location.search,
            }),
          });
        } catch (error) {
          console.warn("Failed to store location:", error);
        }
      };

      storeLocation();
    }

    // 如果用户已认证，重置 ref 以便下次需要时可以再次存储
    if (isAuthenticated) {
      hasStoredLocationRef.current = false;
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
